import React from 'react'

import { faArrowRight } from '@fortawesome/pro-light-svg-icons'
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons/faInfoCircle'
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark'
import Button from '@space-lib/components/button'
import Radio from '@space-lib/components/form/radio'
import Icon from '@space-lib/components/icon'
import Modal from '@space-lib/components/modal'

import ModalBody from '@space-lib/components/modalBody'
import ModalFooter from '@space-lib/components/modalFooter'
import ModalHeader from '@space-lib/components/modalHeader'

import { useTranslation } from 'react-i18next'

import { COMMUNITY_TYPE_DESCRIPTIONS } from './constants'
import {
  ArrowIcon,
  ButtonCloseModal,
  KeepMembersLabel,
  ModalDescription,
  WarningAlert,
  WarningIcon,
  WarningLabel
} from './styles'
import type { ChangeCommunityTypeModalProps } from './types'

import { Divider } from '../../../membersAndPermissions/components/importHistory/components/importHistoryDetailsModal/styles'
import { COMMUNITY_TYPE } from '../../constants'
import CommunityBadge from '../communityBadge/communityBadge'

const ChangeCommunityTypeModal: React.FC<ChangeCommunityTypeModalProps> = ({
  isOpen,
  onClose,
  onConfirmButton,
  communityType,
  pendingType,
  removeUsersOption,
  onRemoveUsersOptionChange,
  isLoading
}) => {
  const { t } = useTranslation()

  return (
    <Modal show={isOpen} onClose={onClose}>
      <ModalHeader className='_px-5 _pt-5 _pb-0'>
        <div className='_d-flex'>
          <p className='_text-4 _text-gray-700 _font-weight-light _m-0 _mr-4'>
            {t('configuration_sidebar.change_community_type_modal.title')}
          </p>
          <ButtonCloseModal
            variation='tertiary'
            className='_p-0 _d-flex _border-0 _bg-transparent _ml-auto _text-gray-400'
            onClick={onClose}>
            <span className='_sr-only'>{'general.close'}</span>
            <Icon icon={faXmark} className='_w-5 _h-5' />
          </ButtonCloseModal>
        </div>
      </ModalHeader>

      <ModalBody className='_px-5'>
        <ModalDescription className='_text-gray-500 _font-weight _mb-6'>
          {t(COMMUNITY_TYPE_DESCRIPTIONS[pendingType])}
        </ModalDescription>
        <div className='_d-flex _w-full _justify-content-center _align-items-center _mb-6'>
          <CommunityBadge type={communityType} />
          <ArrowIcon icon={faArrowRight} className='_my-0 _mx-3' />
          <CommunityBadge type={pendingType} />
        </div>
        {!(communityType === COMMUNITY_TYPE.EXCLUSIVE && pendingType === COMMUNITY_TYPE.OPEN) && (
          <>
            <Divider />
            <KeepMembersLabel className='_text-2 _text-gray-700 _mb-4 _mt-6'>
              {t('configuration_sidebar.change_community_type_modal.keep_members')}
            </KeepMembersLabel>
            <WarningAlert className='_d-flex _p-4 _rounded-lg _mb-4'>
              <WarningIcon icon={faInfoCircle} className='_m-0 _mr-3' />
              <WarningLabel className='_mb-0 _font-weight _text-1'>
                {t('configuration_sidebar.change_community_type_modal.alert_v2')}
              </WarningLabel>
            </WarningAlert>
            <div className='_d-flex _flex-column _mb-4'>
              <div className='_d-flex _mb-2'>
                <Radio
                  id='keepAccess'
                  checked={!removeUsersOption}
                  name='keepAccess'
                  data-test='keepAccess'
                  onChange={() => onRemoveUsersOptionChange(false)}
                />
                <p className='_text-2 _text-gray-600 _font-weight _ml-2 _mb-1'>
                  {t('configuration_sidebar.change_community_type_modal.keepAccess')}
                </p>
              </div>
              <div className='_d-flex'>
                <Radio
                  id='removeAccess'
                  checked={removeUsersOption}
                  name='removeAccess'
                  data-test='removeAccess'
                  onChange={() => onRemoveUsersOptionChange(true)}
                />
                <p className='_text-2 _text-gray-600 _font-weight _ml-2 _mb-0'>
                  {t('configuration_sidebar.change_community_type_modal.removeAccess')}
                </p>
              </div>
            </div>
          </>
        )}
      </ModalBody>

      <ModalFooter className='_d-flex _justify-content-end _pt-0'>
        <Button disabled={isLoading} className='_mr-5' variation='default' onClick={onClose}>
          {t('buttons.cancel')}
        </Button>

        <Button loading={isLoading} onClick={onConfirmButton}>
          {t('general.save')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ChangeCommunityTypeModal
